import { RxEmitterInstance } from '../rxjs/rx-emitter';
import { bcAuthEvents } from 'common/common/constants/auth-events';
import { WebSocketStreamFactory } from 'common/dynamicquery/websocket-stream.factory';
import { StreamConnector } from 'common/dynamicquery/stream-connector';
import { StreamTimers } from 'common/dynamicquery/stream-timers';
import { Injectable, NgZone } from '@angular/core';
import { Deferred } from './deferred';
import { LogService } from 'common/logger/log.service';
import { LoggerService } from 'common/logger/logger.service';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';

const RECONNECT_DELAY_LIMIT_1 = 10;
const RECONNECT_DELAY_LIMIT_2 = 20;
const RECONNECT_DELAY_LIMIT_3 = 30;
const RECONNECT_DELAY_1 = 3000;
const RECONNECT_DELAY_2 = 60000;
const RECONNECT_DELAY_3 = 600000;
const RECONNECT_DELAY_4 = 1800000;

@Injectable({
  providedIn: 'root'
})
export class StreamService {
  private streamConnector;
  private timers;

  private clientInfo;
  private messageCallback;

  private messageHandlers = new Map<string, Function>();
  private channel;
  private deferredChannelReconnect = null;
  private queue = [];
  private _isConnected = false; // to be used when queuing messages
  private _skipReconnect = false;
  private reconnectAttempts = 0;
  private _connectionChangeListeners = [];

  private reconnectTimeout = null;
  private _logger: LoggerService;

  constructor(private _ngZone: NgZone, private _logService: LogService) {
    const stream = WebSocketStreamFactory();
    this.streamConnector = StreamConnector(stream);
    this.timers = StreamTimers();

    this.timers.onConnectionTimeout = this._onConnectionResetTimeout.bind(this);
    this.timers.onHeartbeatTimeout = this._onHeartbeatTimeout.bind(this);
    this._logger = this._logService.getLoggerInstance(StreamService.name);
  }

  public connect(_clientInfo_, _messageCallback_): Promise<any> {
    this.clientInfo = _clientInfo_ || this.clientInfo;
    this.messageCallback = _messageCallback_ || this.messageCallback;

    if (this.channel) {
      this._setDisconnected();
      return this._resetChannel();
    }

    return this._establishChannel()
      .then(channel => this._onChannelEstablished(channel));
    // 'catch'](_onCannotConnect); // TODO
  }

  private _establishChannel() {
    return this.streamConnector.connect(this.clientInfo, this._wrappedMessageCallback.bind(this));
  }

  private _reconnect() {
    if (!this._skipReconnect) {
      this._setDisconnected();

      const deferredReconnect = new Deferred();

      this._tryReconnect(deferredReconnect);

      deferredReconnect.promise.then(() => {
        this._setConnected();
        console.warn('[Stream] successfully reconnected, TS: ', Date.now());
      });
    } else {
      this._skipReconnect = false;
    }
  }

  private _tryReconnect(deferredReconnect) {
    if (this.deferredChannelReconnect) {
      return;
    }
    ++this.reconnectAttempts;
    this.deferredChannelReconnect = this.channel.reconnect();
    this.deferredChannelReconnect.then(() => {
      deferredReconnect.resolve();
      this.deferredChannelReconnect = null;
    }, () => {
      this.deferredChannelReconnect = null;
      console.warn('[Stream] reconnect failed (waiting ' + _getReconnectDelay(this.reconnectAttempts) + ' ms), TS: ', Date.now());
      this.reconnectTimeout = setTimeout(() => {
        this._tryReconnect(deferredReconnect);
      }, _getReconnectDelay(this.reconnectAttempts));
    });

    function _getReconnectDelay(attempts) {
      if (attempts < RECONNECT_DELAY_LIMIT_1) {
        return RECONNECT_DELAY_1;
      } else if (attempts < RECONNECT_DELAY_LIMIT_2) {
        return RECONNECT_DELAY_2;
      } else if (attempts < RECONNECT_DELAY_LIMIT_3) {
        return RECONNECT_DELAY_3;
      }
      return RECONNECT_DELAY_4;
    }
  }

  private _redirect(newUrl) {
    this._setDisconnected();
    this.clientInfo.WebSocketURL = newUrl;
    this._resetChannel();
  }

  public reset() {
    if (!this.channel) {
      return undefined;
    }

    this.close();

    const resetDeferred = new Deferred();
    this._resetChannel()
      .then(() => {
        resetDeferred.resolve();
      })
      .catch((err) => {
        this._logger.logError('Error when resetting channel: {}', err);
        this._handleClosed({ token: 'websetup#error#sessionexpired' });
      });

    return resetDeferred.promise;
  }

  private _resetChannel() {
    return this.channel.reset(this.clientInfo)
      .then(() => {
        this._setConnected();
      });
  }

  public close() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.channel) {
      this.channel.close();
      this._setDisconnected();
    }
  }

  public send(method, params) {
    if (!this._isConnected) {
      return this._sendToQueue(method, params);
    }

    if (!this.channel) {
      return undefined;
    }

    return this._send(method, params);
  }

  public isConnected() {
    return this._isConnected;
  }

  public addListener(listener) {
    this._connectionChangeListeners.push(listener);
  }

  public removeListener(listener) {
    const index = this._connectionChangeListeners.indexOf(listener);
    if (index > -1) {
      this._connectionChangeListeners.splice(index, 1);
    }
  }

  /**
   * You can register to specific events on the websocket specified by its identifier in "method" column.
   * Example: { "method": "remoteControlMessage", "params": ... }
   */
  public registerMessageHandler(methodName, callback) {
    this.messageHandlers.set(methodName, callback);
  }

  public unregisterMessageHandler(methodName) {
    this.messageHandlers.delete(methodName);
  }

  private _send(method, params) {
    const deferred = this.channel.send(method, params);
    return this.timers.setResponseTimer(deferred, { method: method, params: params })
      .then((response) => this._onResponse(response));
  }

  private _sendToQueue(method, params) {
    const deferred = new Deferred();
    this.queue.push({ deferred: deferred, method: method, params: params });
    return deferred.promise;
  }

  private _executeQueue() {
    while (this.queue.length) {
      const item = this.queue.shift();
      this._send(item.method, item.params)
        .then(item.deferred.resolve, item.deferred.reject);
    }
  }

  private _onChannelEstablished(_channel_) {
    this.channel = _channel_;
    this.channel.onConnectionLost = this._onConnectionLost.bind(this);
    this._setConnected();
    return this.channel;
  }

  private _onHeartbeat(id) {
    this.channel.sendAck(id);
    this.timers.resetHeartbeatTimer();
  }

  private _onResponse(response) {
    if (this._isConnected) {
      this.timers.resetHeartbeatTimer();
    }

    return response;
  }

  private _onHeartbeatTimeout() {
    this._reconnect();
  }

  private _onConnectionResetTimeout() {
    if (this.channel.needPeriodicConnectionReset) {
      this._setDisconnected();
      this._resetChannel();
    }
  }

  private _onConnectionLost() {
    console.warn('[Stream] connection lost, TS: ', Date.now());
    this._reconnect();
  }

  private _setConnected() {
    this._isConnected = true;
    this.timers.resetHeartbeatTimer();
    this.timers.resetConnectionTimer();
    this._executeQueue();
    this._onConnectionChanged();
  }

  private _setDisconnected() {
    this._isConnected = false;
    this.timers.stopTimers();
    this._onConnectionChanged();
  }

  private _wrappedMessageCallback(method, params, id, data) {
    this.reconnectAttempts = 0;

    switch (method) {
      case 'heartbeat':
        this._onHeartbeat(id);
        break;
      case 'reconnect':
        this._reconnect();
        break;
      case 'redirect':
        this._redirect(params.WebSocketURL);
        break;
      case 'closed':
        this._handleClosed(params ? params.Message : null);
        break;
      case 'promptAway':
        this._ngZone.run(() => {
          this._emitPromptAway(params, data.params[1]);
        });
        break;
      default:
        if (this.messageHandlers.has(method)) {
          this.messageHandlers.get(method).call(this, params, data);
        } else {
          this.messageCallback(method, params, id, data);
        }
    }
  }

  private _handleClosed(msg): void {
    const message = msg ? msg : { token: 'localclientmanager#logoffrequest#byself' };
    if (this._isOperatorInitiatedLogout(message)) {
      this._skipReconnect = true;
    }

    this._ngZone.run(() => {
      RxEmitterInstance.send(MessageBusEvents.StreamLoginRequired, message);
    });
  }

  private _isOperatorInitiatedLogout(message): boolean {
    return message.token && (message.token.includes('localclientmanager#logoffrequest#byunknownoperator') || message.token.includes('localclientmanager#logoffrequest#byoperator'));
  }

  private _emitPromptAway(serviceType, messageToken) {
    RxEmitterInstance.send(bcAuthEvents.promptAwayRequired, { serviceType, messageToken });
  }

  private _onConnectionChanged() {
    for (let i = 0; i < this._connectionChangeListeners.length; i++) {
      try {
        this._connectionChangeListeners[i]();
      } catch (e) {
        console.warn('[Stream] cannot call listener');
      }
    }
  }
}
